import { Collapse, Divider, Switch, Typography } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import AddIcon from '@mui/icons-material/Add'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined'
import RemoveIcon from '@mui/icons-material/Remove'
import React from 'react'
import { CategoryItemMenu } from './item-menu/item-menu'
import { useStyles } from './style'

const CategoryItem = ({
  index,
  category,
  parentCategory,
  type,
  viewOnly,
  openAll,
  onAddCategory,
  template,
  first,
  last,
  previousCategory,
  nextCategory,
  onManageCategory,
  onSwitchVisibility,
  onRenameCategory,
  onOrderUpCategory,
  onOrderDownCategory,
  onMoveCategory,
  hierarchy
}) => {
  const hasChildren = React.useMemo(() => !!category?.children?.length, [category.children])
  const childrenType = React.useMemo(() => type === 'group' ? 'category' : 'subCategory', [type])
  const classes = useStyles({ hasChildren, type, childrenType })
  const [isChecked, setIsChecked] = React.useState(category.isVisible)
  const [collapsed, setCollapsed] = React.useState(false)

  const handleClick = React.useCallback(() => setCollapsed(collapsed => !collapsed), [setCollapsed])

  React.useEffect(() => { setCollapsed(openAll) }, [openAll])

  const CollapseIcon = () => collapsed
    ? <RemoveIcon className={classes.collapseIcon} />
    : <AddIcon className={classes.collapseIcon} />

  const newItemTxt = React.useMemo(() => childrenType === 'category' ? 'New Category' : 'New Sub Category', [childrenType])

  const handleOpenMoveCategory = React.useCallback(() => onMoveCategory(category, parentCategory, hierarchy), [onMoveCategory, category, parentCategory, hierarchy])
  const handleOpenManageCategory = React.useCallback(() => onManageCategory(category), [onManageCategory, category])
  const handleAddCategory = React.useCallback(() => onAddCategory({ parentCat: category, type: childrenType, startPosition: category.children?.length || 0 }), [onAddCategory, category, childrenType])
  const handleRenameCategory = React.useCallback(() => onRenameCategory(category), [onRenameCategory, category])
  const handleOrderUp = React.useCallback(() => onOrderUpCategory(category, previousCategory, parentCategory), [onOrderUpCategory, category, previousCategory, parentCategory])
  const handleOrderDown = React.useCallback(() => onOrderDownCategory(category, nextCategory, parentCategory), [onOrderDownCategory, category, nextCategory, parentCategory])
  const handleSwitchVisibility = React.useCallback(value => {
    setIsChecked(value)
    const onError = () => setIsChecked(!value)
    onSwitchVisibility(category, value, index, onError)
  }, [onSwitchVisibility, category, index])

  const actions = React.useMemo(() => {
    const data = { rename: handleRenameCategory }
    if (type !== 'group') {
      data.manage = handleOpenManageCategory
      data.move = handleOpenMoveCategory
    }
    if (!first) data.orderUp = handleOrderUp
    if (!last) data.orderDown = handleOrderDown

    return data
  }, [type, first, last, handleRenameCategory, handleOpenManageCategory, handleOrderUp, handleOrderDown, handleOpenMoveCategory])

  return (
    <>
      <Grid container className={classes.root} direction='column' alignContent='center'>
        <Grid container className={classes.name} item xs={10} sx={{ cursor: 'pointer' }} direction='row' alignItems='center' onClick={handleClick}>
          {type !== 'subCategory' && <CollapseIcon />}
          {type === 'category' && <FiberManualRecordIcon className={classes.dotIcon} />}
          {type === 'subCategory' && <FiberManualRecordOutlinedIcon className={classes.dotIcon} />}
          <Typography variant='body2' data-test='category-name'>{category.name}</Typography>
          <Typography variant='body2' data-test='category-list-size'>&nbsp;{category.children ? `(${category.children.length})` : ''}</Typography>
        </Grid>
        {!viewOnly && (
          <Grid container item justifyContent='flex-end' xs={2} alignItems='center'>
            <CategoryItemMenu actions={actions} />
            <Divider orientation='vertical' />
            <Switch
              color='primary'
              checked={isChecked}
              onChange={(_, value) => handleSwitchVisibility(value)} disabled={!onSwitchVisibility}
            />
          </Grid>
        )}
      </Grid>
      <Collapse in={collapsed} className={classes.collapse} data-test='category-listing'>
        {category.children?.map((child, idx) => (
          <CategoryItem
            key={child.sk}
            index={idx}
            first={idx === 0}
            last={(idx + 1) === category.children.length}
            category={child}
            previousCategory={idx > 0 ? category.children[idx - 1] : null}
            nextCategory={idx < category.children.length - 1 ? category.children[idx + 1] : null}
            parentCategory={category}
            type={childrenType}
            viewOnly={viewOnly}
            openAll={openAll}
            template={template}
            onManageCategory={onManageCategory}
            onAddCategory={onAddCategory}
            onRenameCategory={onRenameCategory}
            onOrderUpCategory={onOrderUpCategory}
            onOrderDownCategory={onOrderDownCategory}
            onSwitchVisibility={onSwitchVisibility}
            onMoveCategory={onMoveCategory}
            hierarchy={hierarchy}
          />
        ))}
        {(onAddCategory && !viewOnly && type !== 'subCategory') && (
          <Grid
            item
            container
            className={classes.addCategoryCtn}
            direction='row'
            alignItems='center'
            onClick={handleAddCategory}
          >
            <AddCircleOutlineIcon color='primary' className={classes.addIcon} />
            <Typography variant='body2' color='primary'>{newItemTxt}</Typography>
          </Grid>
        )}
      </Collapse>
    </>
  )
}

export default CategoryItem
